import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useGoogleReviews } from "@/context/GoogleReviewsContext";

/**
 * SyncGoogleReviewsBtn — compact "Sync Google Reviews" pill for the Admin
 * Dashboard header. Same effect as the full-width button in Store panel:
 * invalidates the server cache and then forces a fresh Places JS fetch from
 * the browser so admins can push a just-written review live in one tap.
 */
export default function SyncGoogleReviewsBtn() {
  const { refresh, refreshing } = useGoogleReviews();
  const [busy, setBusy] = useState(false);
  const disabled = busy || refreshing;

  const onClick = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reviews/google/invalidate");
      await refresh({ force: true });
      toast.success("Google reviews synced.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync reviews.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid="admin-sync-google-reviews"
      title="Force a fresh pull of your Google rating + reviews"
      className={`label-xs inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-colors ${
        disabled
          ? "border-line text-espresso-light/50 cursor-wait"
          : "border-line text-espresso-light hover:text-espresso hover:border-espresso"
      }`}
    >
      <RefreshCw size={14} strokeWidth={1.75} className={disabled ? "animate-spin" : ""} />
      <span className="hidden sm:inline">{disabled ? "Syncing…" : "Sync reviews"}</span>
    </button>
  );
}
